/**
 * POI (Points of Interest) Service
 * 
 * Uses Overpass API (OpenStreetMap) to search for nearby places
 * Free alternative to Google Places API
 */

export interface POI {
  id: string
  name: string
  type: string
  category: string
  lat: number
  lng: number
  distance?: number // meters from reference point
  address?: string
  tags: Record<string, string> // OSM tags
  
  // Optional details
  phone?: string
  website?: string
  openingHours?: string
  rating?: number
}

export type POICategory = 
  | 'fuel'
  | 'food'
  | 'accommodation'
  | 'attraction'
  | 'parking'
  | 'rest_area'
  | 'shopping'

interface SearchOptions {
  lat: number
  lng: number
  radius?: number // meters, default 5000 (5km)
  category?: POICategory
  limit?: number
}

/**
 * Search for POIs near a location using Overpass API
 * Includes retry logic for timeout errors
 */
export async function searchNearbyPOIs(options: SearchOptions): Promise<POI[]> {
  const { lat, lng, radius = 5000, category, limit = 20 } = options

  // Build Overpass query based on category
  const query = buildOverpassQuery(lat, lng, radius, category)
  
  const maxRetries = 2
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 || response.status === 503) {
          throw new Error('SERVICE_UNAVAILABLE')
        }
        throw new Error(`Overpass API error: ${response.statusText}`)
      }

      const data = await response.json()
      const pois = parseOverpassResponse(data, lat, lng)
      
      // Sort by distance and limit
      return pois
        .sort((a, b) => (a.distance || 0) - (b.distance || 0))
        .slice(0, limit)
    } catch (error: any) {
      lastError = error
      
      // If it's a timeout or service unavailable, retry
      if (
        (error.message === 'SERVICE_UNAVAILABLE' || 
         error.name === 'AbortError' ||
         error.message.includes('Gateway Timeout')) &&
        attempt < maxRetries
      ) {
        console.log(`POI search attempt ${attempt + 1} failed, retrying...`)
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 2000))
        continue
      }
      
      // If not retryable or max retries reached, throw
      break
    }
  }

  console.error('Failed to search POIs after retries:', lastError)
  throw lastError || new Error('Unknown error searching POIs')
}

/**
 * Search along a route
 */
export async function searchAlongRoute(
  waypoints: { lat: number; lng: number }[],
  category?: POICategory,
  maxDistanceFromRoute = 5000
): Promise<POI[]> {
  // Search around each waypoint
  const allPOIs: POI[] = []
  const seenIds = new Set<string>()

  for (const waypoint of waypoints) {
    const pois = await searchNearbyPOIs({
      lat: waypoint.lat,
      lng: waypoint.lng,
      radius: maxDistanceFromRoute,
      category,
      limit: 10
    })

    // Deduplicate
    for (const poi of pois) {
      if (!seenIds.has(poi.id)) {
        allPOIs.push(poi)
        seenIds.add(poi.id)
      }
    }
  }

  return allPOIs
}

/**
 * Build Overpass QL query
 */
function buildOverpassQuery(
  lat: number, 
  lng: number, 
  radius: number, 
  category?: POICategory
): string {
  const tags = getCategoryTags(category)
  
  // Build node and way queries for each tag
  const queries = tags.map(tag => {
    if (tag.includes('=')) {
      return `node["${tag.split('=')[0]}"="${tag.split('=')[1]}"](around:${radius},${lat},${lng});
              way["${tag.split('=')[0]}"="${tag.split('=')[1]}"](around:${radius},${lat},${lng});`
    } else {
      return `node["${tag}"](around:${radius},${lat},${lng});
              way["${tag}"](around:${radius},${lat},${lng});`
    }
  }).join('\n')

  return `
    [out:json][timeout:25];
    (
      ${queries}
    );
    out center;
  `
}

/**
 * Get OSM tags for category
 */
function getCategoryTags(category?: POICategory): string[] {
  if (!category) {
    return [
      'amenity=fuel',
      'amenity=restaurant',
      'amenity=cafe',
      'amenity=fast_food',
      'tourism=hotel',
      'tourism=motel',
      'tourism=camp_site',
      'tourism=attraction',
      'tourism=museum',
      'amenity=parking',
      'highway=rest_area'
    ]
  }

  const categoryMap: Record<POICategory, string[]> = {
    fuel: [
      'amenity=fuel',
      'amenity=charging_station'
    ],
    food: [
      'amenity=restaurant',
      'amenity=cafe',
      'amenity=fast_food',
      'amenity=bar',
      'amenity=pub'
    ],
    accommodation: [
      'tourism=hotel',
      'tourism=motel',
      'tourism=guest_house',
      'tourism=hostel',
      'tourism=camp_site',
      'tourism=caravan_site'
    ],
    attraction: [
      'tourism=attraction',
      'tourism=museum',
      'tourism=viewpoint',
      'historic=monument',
      'leisure=park'
    ],
    parking: [
      'amenity=parking',
      'amenity=parking_space'
    ],
    rest_area: [
      'highway=rest_area',
      'highway=services',
      'amenity=toilets'
    ],
    shopping: [
      'shop=supermarket',
      'shop=convenience',
      'shop=mall',
      'amenity=marketplace'
    ]
  }

  return categoryMap[category] || []
}

/**
 * Parse Overpass API response
 */
function parseOverpassResponse(data: any, refLat: number, refLng: number): POI[] {
  const elements = data.elements || []
  const pois: POI[] = []

  for (const element of elements) {
    // Get coordinates (for ways, use center)
    const lat = element.lat || element.center?.lat
    const lng = element.lon || element.center?.lon

    if (!lat || !lng) continue

    // Get name
    const name = element.tags?.name || 
                 element.tags?.brand || 
                 element.tags?.operator || 
                 'Unnamed'

    // Determine type and category
    const { type, category } = determineTypeAndCategory(element.tags)

    const poi: POI = {
      id: `osm-${element.type}-${element.id}`,
      name,
      type,
      category,
      lat,
      lng,
      distance: calculateDistance(refLat, refLng, lat, lng),
      tags: element.tags || {},
      address: buildAddress(element.tags),
      phone: element.tags?.phone,
      website: element.tags?.website,
      openingHours: element.tags?.opening_hours
    }

    pois.push(poi)
  }

  return pois
}

/**
 * Determine POI type and category from OSM tags
 */
function determineTypeAndCategory(tags: Record<string, string>): { type: string; category: string } {
  if (tags.amenity === 'fuel') return { type: 'gas_station', category: 'fuel' }
  if (tags.amenity === 'charging_station') return { type: 'ev_charging', category: 'fuel' }
  if (tags.amenity === 'restaurant') return { type: 'restaurant', category: 'food' }
  if (tags.amenity === 'cafe') return { type: 'cafe', category: 'food' }
  if (tags.amenity === 'fast_food') return { type: 'fast_food', category: 'food' }
  if (tags.tourism === 'hotel') return { type: 'hotel', category: 'accommodation' }
  if (tags.tourism === 'motel') return { type: 'motel', category: 'accommodation' }
  if (tags.tourism === 'camp_site') return { type: 'camping', category: 'accommodation' }
  if (tags.tourism === 'attraction') return { type: 'attraction', category: 'attraction' }
  if (tags.tourism === 'museum') return { type: 'museum', category: 'attraction' }
  if (tags.tourism === 'viewpoint') return { type: 'viewpoint', category: 'attraction' }
  if (tags.amenity === 'parking') return { type: 'parking', category: 'parking' }
  if (tags.highway === 'rest_area') return { type: 'rest_area', category: 'rest_area' }
  if (tags.shop) return { type: 'shopping', category: 'shopping' }
  
  return { type: 'other', category: 'other' }
}

/**
 * Build address string from OSM tags
 */
function buildAddress(tags: Record<string, string>): string | undefined {
  const parts = []
  
  if (tags['addr:street']) {
    parts.push(tags['addr:street'])
    if (tags['addr:housenumber']) {
      parts[parts.length - 1] += ' ' + tags['addr:housenumber']
    }
  }
  
  if (tags['addr:city']) parts.push(tags['addr:city'])
  if (tags['addr:postcode']) parts.push(tags['addr:postcode'])
  
  return parts.length > 0 ? parts.join(', ') : undefined
}

/**
 * Calculate distance between two coordinates in meters (Haversine formula)
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return Math.round(R * c) // Distance in meters
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${meters} m`
  }
  return `${(meters / 1000).toFixed(1)} km`
}
